import { Chart } from '@antv/g2';
import { FC, useEffect, useRef, useMemo } from 'react';
import BarChartOptions from './BarChartOptions';
import { ChartCommonProps, ChartConfig, applyConfig } from '../../../utils/BaseChart/BaseChartOptions';

type BarChartProps = ChartCommonProps & {
    barsConfig: ChartConfig[];
    barClickHandler ?: (...args: any[])=> any;
    onChartReady?: (chart: Chart) => void;
};

const BarChart: FC<BarChartProps> = (props) => {
    const { className, data, barsConfig, sizeAttributes, viewLevelAdditionalProps, additionalMarks, barClickHandler, onChartReady} = props;
    const chartContainer = useRef<HTMLDivElement | null>(null);
    const chartInstance = useRef<Chart | null>(null);

    // Memoize chart options to prevent unnecessary recalculations
    const chartOptions = useMemo(() => {
        const barIDs = barsConfig.map((_, index) => `bar${index + 1}`);
        const options = new BarChartOptions(data, barIDs);

        // Set dimension attributes if available
        if (sizeAttributes) options.setAttributes(sizeAttributes);

        if(additionalMarks && additionalMarks.length) options.setChildren(additionalMarks);

        // Apply configurations for each bar
        barsConfig.forEach((barConfig, index) => {
            const barKey = `bar${index + 1}`;

            applyConfig(options, barConfig, barKey);
        });

        // Set view-level additional props if available
        if (viewLevelAdditionalProps) {
            options.setViewAdditionalProps(viewLevelAdditionalProps);
        }

        return options.getOptions();
    }, [data, barsConfig, sizeAttributes, viewLevelAdditionalProps, additionalMarks]);

    useEffect(() => {
        if (chartContainer.current) {
            chartInstance.current = new Chart({ container: chartContainer.current });
            barClickHandler && chartInstance.current.on("interval:click", barClickHandler)
            chartInstance.current.options(chartOptions);
            chartInstance.current
                .render()
                .then(() => {
                    chartInstance.current?.forceFit?.();
                    if (chartInstance.current && onChartReady) {
                        onChartReady(chartInstance.current);
                    }
                })
                .catch((error) => console.error('Render failed', error));
        }

        return () => {
            chartInstance.current?.destroy();
            chartInstance.current && barClickHandler && chartInstance.current.off("interval:click", barClickHandler)
        };
    }, [chartOptions, barClickHandler]);

    // ResizeObserver: G2's autoFit only listens to window resize, not container resize.
    // When the right panel compresses (e.g. left panel expanded), the chart container shrinks
    // but the window doesn't resize. We must call forceFit() when the container resizes.
    useEffect(() => {
        const container = chartContainer.current;
        const chart = chartInstance.current;
        if (!container || !chart?.forceFit) return;

        const resizeObserver = new ResizeObserver(() => {
            chart.forceFit?.();
        });
        resizeObserver.observe(container);
        return () => resizeObserver.disconnect();
    }, [chartOptions]);

    return <div ref={chartContainer} className={`w-full min-w-0 ${className}`} data-test={props["data-test"]} data-asper-barchart/>;
};

export default BarChart;
